import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { Bell, BellOff, BellRing } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const b64ToU8 = (s) => { const p = '='.repeat((4 - (s.length % 4)) % 4); const b = atob((s + p).replace(/-/g, '+').replace(/_/g, '/')); return Uint8Array.from([...b].map(c => c.charCodeAt(0))); };
const isIOS = /iphone|ipad|ipod/i.test(navigator.userAgent);
const standalone = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;

export const PushToggle = ({ isRTL }) => {
  const { token, isAdmin } = useAuth();
  const [state, setState] = useState('unknown'); // unsupported | off | on | denied
  const [busy, setBusy] = useState(false);
  const headers = { Authorization: `Bearer ${token}` };
  const supported = 'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;
  useEffect(() => {
    if (!isAdmin) return;
    if (!supported) { setState('unsupported'); return; }
    if (Notification.permission === 'denied') { setState('denied'); return; }
    navigator.serviceWorker.register('/sw.js').then(reg => reg.pushManager.getSubscription()).then(sub => setState(sub ? 'on' : 'off')).catch(() => setState('off'));
  }, [isAdmin]);
  const enable = async () => {
    setBusy(true);
    try {
      if (isIOS && !standalone) { toast(isRTL ? 'على آيفون: أضف الموقع إلى الشاشة الرئيسية أولاً (زر المشاركة ← إضافة إلى الشاشة الرئيسية) ثم فعّل الإشعارات من التطبيق المثبت' : 'On iPhone: add to Home Screen first, then enable from the installed app', { duration: 8000 }); setBusy(false); return; }
      const perm = await Notification.requestPermission();
      if (perm !== 'granted') { setState(perm === 'denied' ? 'denied' : 'off'); toast.error(isRTL ? 'لم يتم السماح بالإشعارات' : 'Notifications not allowed'); setBusy(false); return; }
      const reg = await navigator.serviceWorker.register('/sw.js');
      await navigator.serviceWorker.ready;
      const { data } = await axios.get(`${API}/push/vapid-public`);
      const sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: b64ToU8(data.key) });
      await axios.post(`${API}/push/subscribe`, { subscription: sub.toJSON(), ua: navigator.userAgent }, { headers });
      setState('on');
      toast.success(isRTL ? 'تم تفعيل الإشعارات على هذا الجهاز ✅' : 'Notifications enabled on this device');
      axios.post(`${API}/push/test`, {}, { headers }).catch(() => {});
    } catch (e) { toast.error(isRTL ? 'تعذّر تفعيل الإشعارات' : 'Could not enable notifications'); }
    setBusy(false);
  };
  const disable = async () => {
    setBusy(true);
    try {
      const reg = await navigator.serviceWorker.getRegistration('/');
      const sub = await reg?.pushManager.getSubscription();
      if (sub) { await axios.delete(`${API}/push/subscribe`, { data: { endpoint: sub.endpoint }, headers }).catch(() => {}); await sub.unsubscribe(); }
      setState('off'); toast(isRTL ? 'تم إيقاف الإشعارات على هذا الجهاز' : 'Notifications disabled');
    } catch { }
    setBusy(false);
  };
  if (!isAdmin || state === 'unknown' || state === 'unsupported') return null;
  const on = state === 'on';
  return (
    <button onClick={on ? disable : enable} disabled={busy || state === 'denied'} title={state === 'denied' ? (isRTL ? 'الإشعارات محظورة من إعدادات المتصفح' : 'Blocked in browser settings') : on ? (isRTL ? 'الإشعارات مفعّلة — اضغط للإيقاف' : 'Notifications on — click to disable') : (isRTL ? 'فعّل إشعارات المكالمات والرسائل على هذا الجهاز' : 'Enable call & message notifications')}
      className={`h-9 px-2.5 rounded-xl flex items-center gap-1.5 text-xs font-bold transition-colors ${on ? 'bg-emerald-50 text-emerald-700 border border-emerald-200' : state === 'denied' ? 'bg-slate-100 text-slate-400' : 'bg-amber-50 text-amber-700 border border-amber-200 hover:bg-amber-100'}`} data-testid="push-toggle">
      {on ? <BellRing className="w-4 h-4" /> : state === 'denied' ? <BellOff className="w-4 h-4" /> : <Bell className="w-4 h-4 animate-pulse" />}
      <span className="hidden sm:inline">{on ? (isRTL ? 'الإشعارات مفعّلة' : 'Notifications on') : (isRTL ? 'فعّل الإشعارات' : 'Enable notifications')}</span>
    </button>
  );
};
